import React, { useEffect, useRef, useState } from "react";

const C = {
  ink: "#0E1A1A",
  muted: "#12262B",
  faint: "#66748B",
  line: "#E7EAF0",
  lineSoft: "#F1F3F7",
  page: "#F8F9FA",
  brand: "#15A08C",
  brandDark: "#0F6E56",
  brandBg: "#E1F5EE",
  danger: "#A32D2D",
  amber: "#854F0B",
  amberBg: "#FAEEDA",
};

const FONT = "Inter, -apple-system, BlinkMacSystemFont, sans-serif";

export interface EditModalProps {
  isOpen: boolean;
  onClose?: () => void;
  onSave?: () => void | Promise<void>;
  title?: string;
  subtitle?: string;
  iconLetter?: string;
  saving?: boolean;
  saveError?: string | null;
  saveDisabled?: boolean;
  hasUnsavedChanges?: boolean;
  footerContent?: React.ReactNode;
  secondaryAction?: React.ReactNode;
  saveLabel?: string;
  children?: React.ReactNode;
}

export default function EditModal(props: EditModalProps): React.ReactElement | null {
  const isOpen = props.isOpen;
  const onClose = props.onClose;
  const onSave = props.onSave;
  const title = props.title || "Edit";
  const subtitle = props.subtitle;
  const iconLetter = props.iconLetter;
  const saving = props.saving;
  const saveError = props.saveError;
  const saveDisabled = props.saveDisabled;
  const hasUnsavedChanges = props.hasUnsavedChanges;
  const footerContent = props.footerContent;
  const secondaryAction = props.secondaryAction;
  const saveLabel = props.saveLabel || "Save";
  const children = props.children;

  const overlayRef = useRef<HTMLDivElement>(null);
  const modalRef = useRef<HTMLDivElement>(null);

  useEffect(function() {
    if (!isOpen) return;
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return function() {
      document.body.style.overflow = prevOverflow;
    };
  }, [isOpen]);

  useEffect(function() {
    if (!isOpen) return;
    function handleKey(e: KeyboardEvent) {
      if (e.key === "Escape") {
        handleClose();
      }
    }
    window.addEventListener("keydown", handleKey);
    return function() {
      window.removeEventListener("keydown", handleKey);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, hasUnsavedChanges]);

  const [showDiscardConfirm, setShowDiscardConfirm] = useState<boolean>(false);
  function handleClose() {
    if (saving) return;
    if (hasUnsavedChanges) {
      setShowDiscardConfirm(true);
      return;
    }
    onClose && onClose();
  }
  function confirmDiscardClose() {
    setShowDiscardConfirm(false);
    onClose && onClose();
  }
  function cancelDiscardClose() {
    setShowDiscardConfirm(false);
  }

  function handleOverlayClick(e: React.MouseEvent<HTMLDivElement>) {
    if (e.target === overlayRef.current) {
      handleClose();
    }
  }

  if (!isOpen) return null;

  return (
    <>
    <div
      ref={overlayRef}
      onClick={handleOverlayClick}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(14, 26, 26, 0.55)",
        zIndex: 1000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
        fontFamily: FONT,
      }}
    >
      <div
        ref={modalRef}
        role="dialog"
        aria-modal="true"
        aria-label={title}
        style={{
          background: "white",
          borderRadius: 14,
          overflow: "hidden",
          boxShadow: "0 30px 80px rgba(0,0,0,0.4)",
          display: "flex",
          flexDirection: "column",
          width: "100%",
          maxWidth: 720,
          maxHeight: "calc(100vh - 40px)",
        }}
      >
        <div
          style={{
            padding: "20px 28px",
            borderBottom: "1px solid " + C.line,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            background: "white",
            flexShrink: 0,
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: 14, minWidth: 0 }}>
            {iconLetter && (
              <div
                style={{
                  width: 36, height: 36, borderRadius: 10,
                  background: C.brandBg, color: C.brandDark,
                  display: "grid", placeItems: "center",
                  fontSize: 16, fontWeight: 700, flexShrink: 0,
                }}
              >
                {iconLetter}
              </div>
            )}
            <div style={{ minWidth: 0 }}>
              <div style={{ fontSize: 17, fontWeight: 700, color: C.ink, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{title}</div>
              {subtitle && (
                <div style={{ fontSize: 12, color: C.faint, marginTop: 2, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{subtitle}</div>
              )}
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={saving}
            aria-label="Close"
            style={{
              width: 34, height: 34, borderRadius: 8,
              background: "transparent", border: "1px solid " + C.line,
              color: C.muted, cursor: saving ? "not-allowed" : "pointer",
              fontSize: 18, display: "grid", placeItems: "center",
              opacity: saving ? 0.5 : 1, fontFamily: FONT,
            }}
          >
            {"\u2715"}
          </button>
        </div>

        <div style={{ flex: 1, overflowY: "auto", padding: "24px 28px", background: C.page }}>
          {children}
        </div>

        <div style={{ padding: "16px 28px", borderTop: "1px solid " + C.line, background: "white", flexShrink: 0 }}>
          {footerContent && (<div style={{ marginBottom: 14 }}>{footerContent}</div>)}
          {saveError && (
            <div style={{ background: "#FCE9E9", color: C.danger, padding: "10px 12px", borderRadius: 8, fontSize: 12.5, marginBottom: 12 }}>
              {saveError}
            </div>
          )}
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, alignItems: "center" }}>
            {secondaryAction && (<div style={{ marginRight: "auto" }}>{secondaryAction}</div>)}
            <button
              onClick={handleClose}
              disabled={saving}
              style={{
                background: "white", color: C.muted,
                border: "1px solid " + C.line,
                padding: "10px 18px", borderRadius: 10,
                fontSize: 14, fontWeight: 600,
                cursor: saving ? "not-allowed" : "pointer",
                opacity: saving ? 0.5 : 1, fontFamily: FONT,
              }}
            >
              Cancel
            </button>
            <button
              onClick={onSave}
              disabled={saving || saveDisabled}
              style={{
                background: C.ink, color: "white",
                border: "1px solid transparent",
                padding: "10px 22px", borderRadius: 10,
                fontSize: 14, fontWeight: 600,
                cursor: (saving || saveDisabled) ? "not-allowed" : "pointer",
                opacity: (saving || saveDisabled) ? 0.5 : 1, fontFamily: FONT,
              }}
            >
              {saving ? "Saving..." : saveLabel}
            </button>
          </div>
        </div>
      </div>
    </div>

      {showDiscardConfirm && (
        <div onClick={cancelDiscardClose} style={{ position: "fixed", inset: 0, background: "rgba(10,26,30,0.42)", zIndex: 2000, display: "flex", justifyContent: "center", alignItems: "flex-start", paddingTop: 100, fontFamily: "'Plus Jakarta Sans', 'Inter', sans-serif" }}>
          <div onClick={function(e) { e.stopPropagation(); }} style={{ width: 440, background: "#fff", borderRadius: 16, boxShadow: "0 20px 60px rgba(10,26,30,0.28)", overflow: "hidden" }}>
            <div style={{ padding: "28px 28px 20px" }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
                <div style={{ width: 40, height: 40, borderRadius: 10, background: "#FEF6E7", color: "#A67312", display: "grid", placeItems: "center", flexShrink: 0, fontSize: 20, fontWeight: 700 }}>!</div>
                <div>
                  <div style={{ fontSize: 18, fontWeight: 700, color: "#12262B", marginBottom: 4, letterSpacing: "-0.01em" }}>Discard changes?</div>
                  <div style={{ fontSize: 13.5, color: "#12262B", lineHeight: 1.55, fontWeight: 500 }}>You have unsaved changes. If you close now, everything you have entered will be lost.</div>
                </div>
              </div>
            </div>
            <div style={{ padding: "16px 28px", background: "#F4F6F8", display: "flex", justifyContent: "flex-end", gap: 10 }}>
              <button onClick={cancelDiscardClose} style={{ padding: "10px 18px", background: "#fff", color: "#12262B", border: "1.5px solid #12262B", borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Keep editing</button>
              <button onClick={confirmDiscardClose} style={{ padding: "10px 18px", background: "#DC2626", color: "#fff", border: 0, borderRadius: 8, fontWeight: 700, fontSize: 13, cursor: "pointer", fontFamily: "inherit" }}>Discard changes</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export interface CollapsibleSectionProps {
  title: string;
  subtitle?: string;
  defaultOpen?: boolean;
  children?: React.ReactNode;
}

export function CollapsibleSection(props: CollapsibleSectionProps): React.ReactElement {
  const title = props.title;
  const subtitle = props.subtitle;
  const defaultOpen = props.defaultOpen !== false;
  const children = props.children;
  const [open, setOpen] = useState<boolean>(defaultOpen);

  return (
    <div style={{ background: "white", border: "1px solid " + C.line, borderRadius: 12, marginBottom: 14 }}>
      <div
        onClick={function() { setOpen(!open); }}
        style={{ padding: "16px 20px", display: "flex", alignItems: "center", justifyContent: "space-between", cursor: "pointer" }}
      >
        <div>
          <div style={{ fontSize: 15, fontWeight: 700, color: C.ink }}>{title}</div>
          {subtitle && (<div style={{ fontSize: 12, color: C.faint, marginTop: 2 }}>{subtitle}</div>)}
        </div>
        <span style={{ color: C.faint, fontSize: 13 }}>
          {open ? "\u25bc" : "\u25b8"}
        </span>
      </div>
      {open && (
        <div style={{ padding: "0 20px 20px" }}>
          {children}
        </div>
      )}
    </div>
  );
}
